import type { INodeConfigProps } from './index';

const maxTimeoutMs = 30000;
const defaultTimeoutMs = 5000;

/**
 * `code-exec` nodes run sandboxed JavaScript (QuickJS) against their
 * input. Mirrors the runtime schema in
 * `packages/node-types/src/code-exec/index.ts`:
 *   - `code` (required) — function body, `input` is in scope, must return
 *   - `outputKey` (optional) — wraps the return under this key
 *   - `timeoutMs` (optional, default 5000, max 30000)
 *   - `bindings` (optional) — left to the JSON fallback for v1; rarely
 *     used and adding a structured editor here would inflate this file
 *     without serving the 90% case.
 */
export function CodeExecConfig({ config, onChange }: INodeConfigProps) {
  const code = typeof config.code === 'string' ? config.code : '';
  const outputKey = typeof config.outputKey === 'string' ? config.outputKey : '';
  const timeoutMs =
    typeof config.timeoutMs === 'number' && Number.isFinite(config.timeoutMs)
      ? config.timeoutMs
      : defaultTimeoutMs;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <label className="field">
        <span className="field-label">Code (JavaScript)</span>
        <textarea
          className="input"
          rows={10}
          value={code}
          onChange={(e) => onChange({ code: e.target.value })}
          placeholder="const total = input.values.reduce((a, b) => a + b, 0);
return { total };"
          spellCheck={false}
          style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 12 }}
        />
        <span className="field-help">
          Function body. <code className="t-mono">input</code> is in scope; must{' '}
          <code className="t-mono">return</code> an object (or any value if <em>Output key</em> is
          set).
        </span>
      </label>

      <label className="field">
        <span className="field-label">Output key (optional)</span>
        <input
          className="input"
          value={outputKey}
          onChange={(e) => onChange({ outputKey: e.target.value || undefined })}
          placeholder="e.g. result"
        />
        <span className="field-help">
          If set, the returned value is wrapped under this key. Useful when{' '}
          <code className="t-mono">return</code> isn&apos;t an object.
        </span>
      </label>

      <label className="field">
        <span className="field-label">Timeout (ms)</span>
        <input
          className="input"
          type="number"
          min={1}
          max={maxTimeoutMs}
          value={timeoutMs}
          onChange={(e) => {
            const next = Number.parseInt(e.target.value, 10);
            if (!Number.isFinite(next) || next <= 0) {
              onChange({ timeoutMs: defaultTimeoutMs });
              return;
            }
            onChange({ timeoutMs: Math.min(next, maxTimeoutMs) });
          }}
        />
        <span className="field-help">
          Max execution time before the sandbox aborts. Default 5000 ms, hard cap 30000 ms.
        </span>
      </label>
    </div>
  );
}
